import {
  Alert,
  Button,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useState } from 'react'

import { Box } from '@/components/ui'
import { type FeatureFlag, MAX_ROLLOUT_ORGS } from '@/definitions/featureFlags'
import { useConfirm } from '@/hooks/useConfirm'
import { useRollOutFeatureFlag } from '@/hooks/useRollOutFeatureFlag'
import { useSnackbar } from '@/hooks/useSnackbar'
import { getApiErrorMessage } from '@/utils/apiErrors'

import { parseOrgIds } from '../../utils/parseOrgIds'
import { summarizeRollout } from '../../utils/summarizeRollout'
import { RolloutResults } from '../RolloutResults'

const ROLLOUT_FAILED_MESSAGE = 'The rollout could not be run.'

const PASTE_HINT =
  'Paste the organizations to update. Anything id-shaped is read, so the ' +
  'name-then-id lines can be pasted as they are.'

interface RolloutPanelProps {
  flag: FeatureFlag
}

/**
 * Granting one flag to a list of organizations.
 *
 * The list is a paste rather than a picker because that is how it arrives — as a
 * block of text on a ticket — and because there is no cross-organization read of
 * who already holds the flag, so there is nothing to browse. The report that
 * comes back is the answer to "who has it now", and it is kept on screen until
 * the next run rather than reduced to a toast.
 */
export const RolloutPanel = ({ flag }: RolloutPanelProps) => {
  const confirm = useConfirm()
  const { showSuccess, showWarning } = useSnackbar()
  // The report reads off the mutation rather than being mirrored into state: it
  // is cleared when the next run starts, which is exactly when it stops being
  // true — the same lifecycle a useState here would have to reproduce by hand.
  const { mutateAsync, isPending, error, data: report } = useRollOutFeatureFlag()

  const [pasted, setPasted] = useState('')
  const [enable, setEnable] = useState(true)

  const orgIds = parseOrgIds(pasted)
  const isOverLimit = orgIds.length > MAX_ROLLOUT_ORGS
  const rolloutError = error === null ? null : getApiErrorMessage(error, ROLLOUT_FAILED_MESSAGE)

  // Nothing to grant: the flag already reaches every practice without a row.
  const coversEveryone = flag.enabled && flag.enable_all_orgs

  const handleApply = async () => {
    const confirmed = await confirm({
      title: `Turn ${flag.name} ${enable ? 'on' : 'off'} for ${orgIds.length} organizations?`,
      body: (
        <Typography variant='body2'>
          {enable
            ? 'Each one is granted the flag immediately.'
            : 'Each one loses the flag immediately, and any staff currently using it will see the feature disappear.'}
        </Typography>
      ),
      confirmLabel: enable ? 'Turn on' : 'Turn off',
      destructive: !enable,
    })
    if (!confirmed) {
      return
    }

    const response = await mutateAsync({ name: flag.name, data: { org_ids: orgIds, enable } })

    const summary = summarizeRollout(response.results)
    if (summary.needsAttention > 0) {
      showWarning(`${summary.needsAttention} organizations were not changed.`)
    } else {
      showSuccess(`${summary.moved} organizations ${enable ? 'enabled' : 'disabled'}.`)
    }
  }

  return (
    <Paper variant='detailCard'>
      <Box variant='flexColumn' gap='lg'>
        <Box variant='flexColumn' gap='none'>
          <Typography variant='h6'>Roll out to organizations</Typography>
          {!coversEveryone && (
            <Typography variant='body2' color='text.secondary'>
              {PASTE_HINT}
            </Typography>
          )}
        </Box>

        {coversEveryone && (
          <Alert severity='info'>
            This flag is on for all organizations, so there is nothing to roll out
            — every practice already has it, and a per-organization row can neither
            add to that nor take it away. To narrow it, turn off &apos;On for all
            orgs&apos; above and then roll it out to the organizations that should
            keep it.
          </Alert>
        )}

        {!coversEveryone && (
          <>
            {rolloutError !== null && <Alert severity='error'>{rolloutError}</Alert>}

            <ToggleButtonGroup
              exclusive
              size='small'
              value={enable}
              onChange={(_event, next: boolean | null) => {
                // Null when the pressed button was already selected; the direction
                // must stay chosen either way.
                if (next !== null) {
                  setEnable(next)
                }
              }}
            >
              <ToggleButton value={true}>Turn on</ToggleButton>
              <ToggleButton value={false}>Turn off</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              label='Organization ids'
              multiline
              minRows={6}
              fullWidth
              value={pasted}
              onChange={(event) => setPasted(event.target.value)}
            />

            <Box variant='flexBetween'>
              <Typography variant='caption' color={isOverLimit ? 'error' : 'text.secondary'}>
                {isOverLimit
                  ? `${orgIds.length} organizations read — at most ${MAX_ROLLOUT_ORGS} can be rolled out at once.`
                  : `${orgIds.length} organizations read`}
              </Typography>
              <Button
                variant='contained'
                color={enable ? 'primary' : 'warning'}
                disabled={orgIds.length === 0 || isOverLimit || isPending}
                onClick={handleApply}
              >
                {isPending ? 'Applying...' : enable ? 'Turn on' : 'Turn off'}
              </Button>
            </Box>

            {report !== undefined && <RolloutResults results={report.results} />}
          </>
        )}
      </Box>
    </Paper>
  )
}
